import { Controller, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { FormControlLabel, Switch } from '@material-ui/core';
import { ToggleButton } from '@material-ui/lab';
import { TimesheetFilterBarProps, TimesheetStatusFilter } from './types';
import {
  getStatusCount,
  isApproverLockedToAll,
  TIMESHEET_STATUS_FILTERS,
} from './utils';
import {
  FilterActions,
  FilterBar,
  FilterNote,
  SegmentedControl,
  SegmentedCount,
} from './Timesheet.css';

const APPROVER_OPTIONS = [
  { value: 'mine', label: 'TIMESHEET_FILTER.APPROVER.MINE' },
  { value: 'all', label: 'TIMESHEET_FILTER.APPROVER.ALL' },
];

const STATUS_OPTIONS = TIMESHEET_STATUS_FILTERS.map((value) => ({
  value,
  label: `TIMESHEET_FILTER.STATUS.${value.toUpperCase()}`,
}));

const TimesheetFilterBar = ({
  control,
  canSeeAll,
  statusCounts,
  isClientOrProjectSelected,
  loading,
  leading,
  actions,
}: TimesheetFilterBarProps) => {
  const intl = useIntl();
  const message = (id: string) => intl.formatMessage({ id });
  const status = useWatch({
    control,
    name: 'status',
  }) as TimesheetStatusFilter;
  const approverLocked = isApproverLockedToAll(status, canSeeAll);

  const renderSegmented = (
    name: 'approver' | 'status',
    options: { value: string; label: string }[],
    disabled = false,
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ value, onChange }) => (
        <SegmentedControl
          exclusive
          size="small"
          value={value}
          onChange={(_, selected) => {
            if (!selected) {
              return;
            }
            if (
              name === 'status' &&
              isApproverLockedToAll(selected, canSeeAll)
            ) {
              control.setValue('approver', 'all');
            }
            onChange(selected);
          }}
          aria-label={message(
            name === 'approver'
              ? 'TIMESHEET_FILTER.APPROVER'
              : 'TIMESHEET_FILTER.STATUS',
          )}
        >
          {options.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              disabled={loading || disabled}
              id={`timesheet-filter-${name}-${option.value}`}
            >
              {message(option.label)}
              {name === 'status' && statusCounts && (
                <SegmentedCount>
                  {getStatusCount(
                    option.value as TimesheetStatusFilter,
                    statusCounts,
                  )}
                </SegmentedCount>
              )}
            </ToggleButton>
          ))}
        </SegmentedControl>
      )}
    />
  );

  return (
    <FilterBar>
      {leading}
      {renderSegmented('status', STATUS_OPTIONS)}
      {canSeeAll ? (
        renderSegmented('approver', APPROVER_OPTIONS, approverLocked)
      ) : (
        <FilterNote>{message('TIMESHEET_FILTER.APPROVER.NOTE')}</FilterNote>
      )}
      {isClientOrProjectSelected && (
        <Controller
          name="showFullTimesheet"
          control={control}
          render={({ value, onChange }) => (
            <FormControlLabel
              label={message('TABLE_FILTER.LABEL.SHOW_FULL_TIMESHEET')}
              control={
                <Switch
                  id="timesheet-filter-showFullTimesheet"
                  color="primary"
                  checked={!!value}
                  disabled={loading}
                  onChange={(event) => onChange(event.target.checked)}
                />
              }
            />
          )}
        />
      )}
      {actions && <FilterActions>{actions}</FilterActions>}
    </FilterBar>
  );
};

export default TimesheetFilterBar;
